<template lang="pug">
  validation-observer(
    ref="obs"
    slim
    v-bind="observerProps"
    v-slot="{ errors, invalid, handleSubmit, validate }"
  )
    form(
      :class="formClass"
      @submit.prevent="handleSubmit(save)"
    )
      slot(
        name="prepend-fields"
        :update="update"
      )
      template(v-for="field in fields")
        slot(
          name="before-field"
          :field="field"
        )
        validation-provider(
          :rules="field.rules"
          :name="field.text"
          :vid="field.pkey || field.value"
          :key="field.pkey || field.value"
          slim
          v-slot="{ errors, valid }"
        )
          component(
            :is="field.component"
            v-bind="getFieldAttrs(field)"
            v-on="field.component_events"
            v-model="instance[field.value]"
            :key="field.key"
            :name="field.value"
            :error-messages="errors"
            :label="!field.hideLabel ? field.text : undefined"
            @change="update(field.value, $event)"
            @hook:mounted="reset({ ...instance })"
          )
            template(
              v-for="name in fieldsSlots[field.value]"
              v-slot:[name]
            )
              slot(
                :name="`${field.value}.${name}`"
                v-bind=`{
                  form: {
                    instance,
                    invalid,
                    loading,
                    action: () => handleSubmit(save),
                  },
                }`
              )

      slot(
        name="append-fields"
        :update="update"
      )
      slot(
        name="preppend-actions"
        v-bind="{ reset }"
      )

      template(v-if="!hideSaveBtn")
        slot(
          name="save-btn"
          v-bind="{ attrs: saveBtnAttrs, errors, invalid, loading, disabled, validate, hasChanges, action: () => handleSubmit(save) }"
        )
          app-button(
            type="submit"
            rounded
            depressed
            v-bind="btnProps"
            :loading="loading"
          ) {{ btnProps.btnText || 'Сохранить' }}

      slot(
        name="append-actions"
        v-bind="{ reset }"
      )
</template>

<script>
/**
 *  REFACTOR:
 *  - Избавиться от hook:mounted
 *  - Придумать что-то для кнопок, которые через слот используют портал (не работает submit, потому что они в другой части DOM-дерева)
 */

export default {
  name: 'ApiForm',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    method: {
      type: Function,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    initial: {
      type: Object,
      default: () => ({}),
    },
    successCallback: {
      type: Function,
      default: () => {},
    },
    errorCallback: {
      type: Function,
      default: () => {},
    },
    serializeData: {
      type: Function,
      default: (data) => data,
    },
    btnProps: {
      type: Object,
      default: () => ({}),
    },
    genericFieldAttrs: {
      type: Object,
      default: () => ({}),
    },
    noReset: {
      type: Boolean,
      default: false,
    },
    observerProps: {
      type: Object,
      default: () => ({}),
    },
    hideSaveBtn: {
      type: Boolean,
      default: false,
    },
    formClass: {
      type: [String, Array, Object],
      default: undefined,
    },
  },
  data() {
    return {
      obs: null,
      loading: false,
      bufferValue: { ...this.value },
    };
  },
  computed: {
    instance: {
      get() {
        return this.bufferValue;
      },
      set(value) {
        this.bufferValue = value;
        this.$emit('input', value);
      },
    },
    disabled() {
      const { obs } = this.$refs;
      if (!obs) {
        return false;
      }

      const { errors, flags } = obs;
      return this.isDisabled(errors, flags.invalid);
    },
    fieldsSlots() {
      const { $scopedSlots, $slots } = this;
      const getSlots = (slots) =>
        Object.keys(slots).reduce((acc, key) => {
          const [fieldKey, name] = key.split('.');
          if (fieldKey) {
            if (!acc[fieldKey]) acc[fieldKey] = [];
            acc[fieldKey].push(name);
          }
          return acc;
        }, {});
      return {
        ...getSlots($scopedSlots),
        ...getSlots($slots),
      };
    },
    saveBtnAttrs() {
      return {
        type: 'submit',
      };
    },
    hasChanges() {
      return JSON.stringify(this.initial) !== JSON.stringify(this.bufferValue);
    },
  },
  mounted() {
    this.reset();
  },
  methods: {
    async save() {
      this.loading = true;

      const payload = this.serializeData(this.instance);
      const { obs } = this.$refs;

      try {
        const response = await this.method({
          data: payload,
          obs,
        });
        this.$emit('response', { response });
        if (!response.error && response instanceof Error !== true) {
          this.successCallback(response.response || response);

          if (!this.noReset) this.reset();
        } else {
          throw new Error(response.error);
        }
      } catch (e) {
        this.errorCallback(e);
      } finally {
        this.loading = false;
      }
    },
    update(key, value) {
      if (key !== undefined) {
        this.instance = {
          ...this.instance,
          [key]: value,
        };
        this.$emit('update', {
          value: this.instance,
          action: () => this.$refs.obs.handleSubmit(this.save),
        });
      }
    },
    reset(newInitial = this.initial) {
      const { obs } = this.$refs;
      if (!obs) {
        return;
      }

      obs.reset();
      this.instance = { ...newInitial };
    },
    isDisabled(errors, invalid) {
      const hasErrors = !!Object.values(errors).filter((val) => val.length).length;
      return hasErrors || invalid;
    },
    hasDeepFields(field) {
      return !!field.fields?.length;
    },
    getFieldAttrs(field) {
      const { rules, component_attrs } = field;
      const fieldRulesObj = typeof rules === 'object' ? rules : Object.values(
        (rules || '').split('|').filter(Boolean),
      ).reduce((acc, rule) => {
        let output = true;
        const [name, val] = rule.split(':');

        if (typeof val === 'string') {
          const args = val.split(',');
          output = args.length > 1 ? args : args.at(0);
        }

        return { ...acc, [name]: output };
      }, {});
      return {
        ...this.genericFieldAttrs,
        required: Object.keys(fieldRulesObj).some((rule) => rule.startsWith('required')),
        ...component_attrs,
      };
    },
  },
};
</script>
